/**
 * `gg.js` resolver — parses hitomi.la's per-deploy JavaScript helper that
 * determines which CDN subdomain serves a given image hash.
 *
 * The file is small (~5-10 KB) and contains:
 *
 *   ```
 *   var o = <0|1>;
 *
 *   function m(g) {
 *     var o = <0|1>;
 *     switch (g) {
 *       case <code>:
 *       case <code>:
 *         o = <0|1>; break;
 *     }
 *     return o;
 *   }
 *
 *   function b(...) { ... }
 *   var b = '<unix-ts>/';
 *   ```
 *
 * We never `eval` it (security + CSP) — instead we string-scan the source
 * for the bits we need. The format is stable enough that this has held for
 * years, but if hitomi reshapes the file we'll throw a {@link ParseError}.
 *
 * The IO side (fetch + cache in `meta` IndexedDB store) lives at the bottom of
 * this file; the pure parser + URL builder above are the testable surface.
 */

import { ParseError } from '../../lib/errors.ts';
import { proxiedImageUrl } from '../../lib/proxy.ts';
import type { GgContext, HitomiExtension, HitomiImage } from './types.ts';

/**
 * Hostname for static hitomi resources (gg.js, nozomi files, B-tree index,
 * `/galleries/{id}.js` JSONP). Used by the fetcher and URL builder.
 *
 * FRAGILE: in March 2025 hitomi.la migrated this CDN from
 * `ltn.hitomi.la` to `ltn.gold-usergeneratedcontent.net`. The old host
 * stopped resolving from CF's egress (Origin DNS error). If hitomi
 * migrates again the symptom will be uniform 5xx from every hitomi
 * endpoint; cross-reference the gallery-dl / keiyoushi-extensions repos
 * for the current host.
 */
export const RESOURCE_DOMAIN = 'ltn.gold-usergeneratedcontent.net';
/**
 * Base domain for image / thumbnail CDN subdomains (`a1.…`, `w2.…`, `tn.…`).
 *
 * FRAGILE: migrated in lockstep with `RESOURCE_DOMAIN` — the per-deploy
 * `gg.js` selects subdomains `a1`/`a2`/`w1`/`w2` etc., which are joined
 * to this base.
 */
export const BASE_DOMAIN = 'gold-usergeneratedcontent.net';

/** Default cache lifetime for the parsed `gg.js`. Hitomi refreshes infrequently. */
export const GG_CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * Parse the contents of `https://ltn.hitomi.la/gg.js` into a {@link GgContext}.
 *
 * This function is pure: it takes the raw source text and returns the parsed
 * triple. No network, no DOM, no eval.
 *
 * @throws {ParseError} if any of the load-bearing tokens are missing.
 */
export function parseGgJs(source: string): GgContext {
  return {
    subdomainCodes: extractSubdomainCodes(source),
    subdomainFlag: extractSubdomainFlag(source),
    pathPrefix: extractPathPrefix(source),
    parsedAt: Date.now(),
  };
}

/**
 * Scan for every `case <integer>:` token and collect the integers. We deliberately
 * accept extra whitespace between `case` and the digits.
 */
function extractSubdomainCodes(source: string): Set<number> {
  const codes = new Set<number>();
  const re = /case\s+(\d+)\s*:/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source)) !== null) {
    const digits = m[1];
    if (!digits) continue;
    const n = Number.parseInt(digits, 10);
    if (Number.isInteger(n)) codes.add(n);
  }
  if (codes.size === 0) {
    throw new ParseError('gg.js parser: no `case <num>:` arms found', {
      source: 'hitomi',
      snippet: snippet(source),
    });
  }
  return codes;
}

/**
 * The variable controls which "side" of the case-match goes to subdomain 2 vs 1.
 * The upstream uses `var o = X;` at the function head and we honor that. If
 * multiple `var o` declarations are present we take the FIRST one — this
 * matches the file-top variable that participates in the URL choice.
 */
function extractSubdomainFlag(source: string): boolean {
  const m = /var\s+o\s*=\s*(\d+)\s*;/.exec(source);
  if (!m || !m[1]) {
    throw new ParseError('gg.js parser: missing `var o = X;` declaration', {
      source: 'hitomi',
      snippet: snippet(source),
    });
  }
  const n = Number.parseInt(m[1], 10);
  if (!Number.isInteger(n)) {
    throw new ParseError(`gg.js parser: \`var o\` not an integer: ${m[1]}`, {
      source: 'hitomi',
      snippet: snippet(source),
    });
  }
  return !n;
}

/**
 * Path prefix from `b: '...'` (a colon-style member, the upstream's convention).
 * We accept either single or double quotes. The trailing slash is preserved
 * because the upstream uses it as part of URL composition.
 */
function extractPathPrefix(source: string): string {
  const m = /b:\s*['"]([^'"]+)['"]/.exec(source);
  if (!m || !m[1]) {
    throw new ParseError("gg.js parser: missing `b: '...'` path prefix", {
      source: 'hitomi',
      snippet: snippet(source),
    });
  }
  return m[1];
}

function snippet(source: string): string {
  return source.length > 200 ? `${source.slice(0, 200)}...` : source;
}

/**
 * Derive the integer "hash code" used as the lookup key in {@link GgContext.subdomainCodes}.
 *
 * Hitomi takes the last three hex characters of an image's hash and reorders
 * them as `<last><second-to-last><third-to-last>`, then parses as base-16.
 * This is faithfully `parseInt(hash[-1] + hash[-3..-1], 16)`.
 *
 * Returns `NaN` if the hash is too short or non-hex; callers should treat that
 * as a parse failure upstream.
 */
export function deriveHashCode(hash: string): number {
  if (hash.length < 3) return Number.NaN;
  const last = hash.slice(-1);
  const twoBefore = hash.slice(-3, -1);
  return Number.parseInt(last + twoBefore, 16);
}

export interface ComputeImageUrlOpts {
  /** `'avif'` (default) or `'webp'`. `'jxl'` exists in the schema but is always false. */
  extension?: HitomiExtension;
  /** Override the `hitomi.la` base for testing — leave undefined in production. */
  baseDomain?: string;
}

/**
 * Build the fully-qualified `https://` URL for an image.
 *
 * The formula (faithfully ported from the wire format, not the upstream code)
 * is:
 *
 * ```
 * subdomain = <ext-initial> + (subdomainCodes.has(hashCode) === subdomainFlag ? '2' : '1')
 * path     = <pathPrefix><hashCode>/<hash>.<ext>
 * url      = https://<subdomain>.<baseDomain>/<path>
 * ```
 *
 * where `subdomainFlag` is `!o` from `var o = <0|1>;` in `gg.js`. The
 * `===` test is an `nxor` — when the membership check matches the flag we
 * land on `*2`; otherwise `*1`.
 */
export function computeImageUrl(
  image: Pick<HitomiImage, 'hash' | 'hasAvif'>,
  context: GgContext,
  opts: ComputeImageUrlOpts = {},
): string {
  const extension: HitomiExtension =
    opts.extension ?? (image.hasAvif ? 'avif' : 'webp');
  const baseDomain = opts.baseDomain ?? BASE_DOMAIN;

  const hashCode = deriveHashCode(image.hash);
  if (!Number.isInteger(hashCode)) {
    throw new ParseError(`hitomi: bad image hash "${image.hash}"`, {
      source: 'hitomi',
    });
  }

  const matchesFlag =
    context.subdomainCodes.has(hashCode) === context.subdomainFlag;
  const subdomain = `${extension[0]}${matchesFlag ? '2' : '1'}`;
  const path = `${context.pathPrefix}${hashCode}/${image.hash}.${extension}`;

  // Hitomi's image CDNs hot-link-protect against any non-hitomi.la
  // Referer; browsers can't spoof Referer on cross-origin `<img>` loads.
  // Route through the worker proxy where `refererFor()` substitutes the
  // right value. Same constraint as `coverUrlFor` in `parsers.ts`.
  return proxiedImageUrl(`https://${subdomain}.${baseDomain}/${path}`);
}

/**
 * IO side: fetch, cache, and return a parsed {@link GgContext}.
 *
 * The runtime imports live behind a lazy import so this module can be loaded
 * (e.g. by unit tests) without dragging in `proxyFetch` / IndexedDB / the
 * `@` path alias. Tests exercise {@link parseGgJs} and {@link computeImageUrl}
 * directly, never {@link getImageContext} / {@link getImageUrl}.
 *
 * Cache layers (in order):
 *   1. Module-local memory.
 *   2. IndexedDB `meta` store, key `hitomi-gg`.
 *   3. Network fetch via the proxy.
 */
let memoCache: GgContext | undefined;
let inflight: Promise<GgContext> | undefined;

export const GG_DB_KEY = 'hitomi-gg';

interface CachedGgRow {
  parsedAt: number;
  subdomainCodes: number[];
  subdomainFlag: boolean;
  pathPrefix: string;
}

export async function getImageContext(opts?: { ttlMs?: number }): Promise<GgContext> {
  const ttl = opts?.ttlMs ?? GG_CACHE_TTL_MS;
  if (memoCache && Date.now() - memoCache.parsedAt < ttl) return memoCache;
  if (inflight) return inflight;

  inflight = (async () => {
    const fromIdb = await loadFromIdb();
    if (fromIdb && Date.now() - fromIdb.parsedAt < ttl) {
      memoCache = fromIdb;
      return fromIdb;
    }
    const fresh = await fetchAndParse();
    memoCache = fresh;
    await saveToIdb(fresh).catch(() => {
      // IndexedDB persistence is best-effort. We can always re-fetch.
    });
    return fresh;
  })().finally(() => {
    inflight = undefined;
  });
  return inflight;
}

async function fetchAndParse(): Promise<GgContext> {
  const { proxyFetch } = await import('@/lib/proxy');
  const res = await proxyFetch(`https://${RESOURCE_DOMAIN}/gg.js`, {
    debugLabel: 'hitomi.gg.js',
  });
  if (!res.ok) {
    throw new ParseError(`hitomi: gg.js fetch returned ${res.status}`, {
      source: 'hitomi',
    });
  }
  const text = await res.text();
  return parseGgJs(text);
}

async function loadFromIdb(): Promise<GgContext | undefined> {
  try {
    const { getDb } = await import('@/db/schema');
    const db = await getDb();
    const row = await db.get('meta', GG_DB_KEY);
    if (!row) return undefined;
    const raw = row.value as CachedGgRow | undefined;
    if (!raw || !Array.isArray(raw.subdomainCodes)) return undefined;
    return {
      subdomainCodes: new Set(raw.subdomainCodes),
      subdomainFlag: Boolean(raw.subdomainFlag),
      pathPrefix: String(raw.pathPrefix),
      parsedAt: Number(raw.parsedAt) || 0,
    };
  } catch {
    return undefined;
  }
}

async function saveToIdb(ctx: GgContext): Promise<void> {
  const { getDb } = await import('@/db/schema');
  const db = await getDb();
  const payload: CachedGgRow = {
    parsedAt: ctx.parsedAt,
    subdomainCodes: [...ctx.subdomainCodes],
    subdomainFlag: ctx.subdomainFlag,
    pathPrefix: ctx.pathPrefix,
  };
  await db.put('meta', { key: GG_DB_KEY, value: payload });
}

/**
 * Convenience wrapper: resolve the final image URL for a single page in one
 * call. Used by `api.ts` and the `ComicSource.resolveImageUrl` impl.
 */
export async function getImageUrl(
  image: HitomiImage,
  opts: ComputeImageUrlOpts = {},
): Promise<string> {
  const ctx = await getImageContext();
  return computeImageUrl(image, ctx, opts);
}

/**
 * Reset module-local cache. Intended for tests and manual "force refresh"
 * UX (e.g. a debug button on Settings).
 */
export function _resetGgCache(): void {
  memoCache = undefined;
  inflight = undefined;
}
